import React, { useEffect, useMemo, useState } from "react";
import "./styles.css";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { app } from "../../services";
import moment from "moment";

const Selector = ({
  value = { vc_nombre: "Seleccione" },
  colors = [],
  keyField = "vc_nombre",
  measuresSelected = [],
  options = [],
  onSelect = () => {},
  onSelectGroups = () => {},
  onDelete = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [clasesStruct, setClasesStruct] = useState([]);

  // Comparador de selección por id (evita fallas por referencias distintas)
  const selectedIds = useMemo(() => {
    const ids = new Set();
    (measuresSelected || []).forEach((m) => m?.id && ids.add(m.id));
    return ids;
  }, [measuresSelected]);

  // Ordena chips (sin mutar measuresSelected original)
  const measuresSelectedSorted = useMemo(() => {
    return (measuresSelected || [])
      .slice()
      .sort((a, b) =>
        (a?.vc_tag || a?.vc_fieldName || "").localeCompare(
          b?.vc_tag || b?.vc_fieldName || ""
        )
      );
  }, [measuresSelected]);

  useEffect(() => {
    let alive = true;

    const getOrderMeasures = async () => {
      try {
        // Si no hay options, no tiene sentido construir árbol
        if (!options?.length) {
          if (alive) setClasesStruct([]);
          return;
        }

        const res = await app.service("classes").find({
          query: {
            $limit: 100,
            $skip: 0,
            $and: [{ id_tipo: "2d2e3808-e401-4cd2-90fb-bebf304882d8" }],
            $sort: { createdAt: "ASC" },
          },
        });

        if (!alive) return;

        const classesData = res?.data || [];

        // Construye set de clases necesarias (clase del measure + ancestros)
        const classById = new Map(classesData.map((c) => [c.id, c]));

        const measObjectById = {};
        options.forEach((opt) => {
          const clas = classById.get(opt?.id_clase);
          if (!clas) return;

          measObjectById[clas.id] = clas;

          let father = classById.get(clas.id_clase);
          while (father) {
            measObjectById[father.id] = father;
            father = classById.get(father.id_clase);
          }
        });

        const neededClasses = Object.values(measObjectById);

        // Ordena por createdAt (numérico, sin moment para restar objetos)
        const clases = neededClasses.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        const getChildren = (clase) => {
          const measures = (options || [])
            .filter((e) => e.id_clase === clase.id)
            .slice()
            .sort((a, b) => (a.vc_nombre > b.vc_nombre ? 1 : -1));

          return {
            ...clase,
            measures,
            childrens: clases
              .filter((e) => e.id_clase === clase.id)
              .map((c) => getChildren(c)),
          };
        };

        const clasesOrder = clases
          .filter((e) => e.id_clase === null)
          .map((e) => getChildren(e));

        if (!alive) return;
        setClasesStruct(clasesOrder);
      } catch (error) {
        if (!alive) return;
        console.error("Error fetching data:", error);
        setClasesStruct([]);
      }
    };

    getOrderMeasures();

    return () => {
      alive = false;
    };
  }, [options]);

  const renderItem = (item, index) => {
    const isSelected = !!item?.id && selectedIds.has(item.id);

    return (
      <MenuItem
        key={item?.id ? `measure-${item.id}` : `measure-${index}`}
        onClick={() => {
          setAnchorEl(null);
          onSelect(item);
        }}
      >
        <Grid container>
          <Grid item xs style={{ paddingTop: 8 }}>
            {item?.[keyField]}
          </Grid>

          <Grid item xs minWidth={10} />

          <Grid item xs="auto">
            <Box
              p={1}
              flexDirection={"row"}
              justifyContent="center"
              display="flex"
              style={{ height: "100%", fontSize: "0.8rem", fontWeight: 700 }}
            >
              <Typography color="primary">
                {item?.medida?.vc_abreviatura}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs="auto">
            <Checkbox
              size="small"
              color="primary"
              onChange={() => onSelect(item)}
              checked={isSelected}
              style={{ alignSelf: "end", marginRight: -12 }}
            />
          </Grid>
        </Grid>
      </MenuItem>
    );
  };

  const renderSections = (item, index) => {
    const itemMeasures = item?.measures || [];
    const selectedCount = itemMeasures.filter((m) => m?.id && selectedIds.has(m.id))
      .length;

    const allSelected = itemMeasures.length > 0 && selectedCount === itemMeasures.length;

    return (
      <Box key={item?.id ? `section-${item.id}` : `section-${index}`}>
        <ListItem
          style={{
            backgroundColor:
              item?.id_clase === null ? "transparent" : "rgba(0,0,0,0.1)",
            borderRadius: 10,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography color={"primary"} fontWeight={700}>
            {item?.[keyField]}
          </Typography>

          {(item?.childrens?.length || 0) === 0 && (
            <ListItemIcon style={{ justifyContent: "end" }}>
              <Checkbox
                size="small"
                color="primary"
                onClick={(e) => {
                  onSelectGroups(item);
                  setAnchorEl(null);
                  e.preventDefault();
                }}
                onChange={(e) => {
                  onSelectGroups(item);
                  setAnchorEl(null);
                  e.preventDefault();
                }}
                checked={allSelected}
                style={{ alignSelf: "end", marginRight: -12 }}
              />
            </ListItemIcon>
          )}
        </ListItem>

        <Paper
          style={
            item?.id_clase === null
              ? {
                  boxShadow: "0px 0px 1px darkGray",
                  borderRadius: 10,
                  margin: 10,
                  overflow: "hidden",
                }
              : {}
          }
        >
          {itemMeasures.map((m, i) => renderItem(m, i))}
          {(item?.childrens || []).map((c, i) => renderSections(c, i))}
        </Paper>
      </Box>
    );
  };

  return (
    <>
      <Box width={"100%"}>
        <Button
          fullWidth
          onClick={(e) => setAnchorEl(e.currentTarget)}
          color={"inherit"}
          style={{ fontWeight: 700, padding: 8 }}
        >
          {measuresSelectedSorted.length > 0 ? `Mediciones` : "Seleccione"}

          <Box
            height={"100%"}
            marginRight={1}
            justifyContent="center"
            display={"flex"}
            flexDirection="column"
            position="absolute"
            top={1}
            right={1}
          >
            <ArrowDropDown />
          </Box>
        </Button>

        <Divider
          style={{ display: measuresSelectedSorted.length > 0 ? "flex" : "none" }}
        />

        <Box
          p={measuresSelectedSorted.length > 0 ? "4px" : 0}
          justifyContent={"space-around"}
        >
          {measuresSelectedSorted.length > 0 &&
            measuresSelectedSorted.map((item, index) => (
              <Chip
                key={item?.id ? `chip-${item.id}` : `chip-${index}`}
                label={item?.vc_tag || item?.vc_fieldName}
                onClick={() => onSelect(item)}
                // onDelete={() => onDelete(item)}
                style={{ margin: 4, border: `2px solid ${colors[index]}` }}
              />
            ))}
        </Box>
      </Box>

      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          "aria-labelledby": "lock-button",
          role: "listbox",
        }}
        style={{ width: anchorEl?.width || "100%" }}
      >
        {!!anchorEl && !!clasesStruct.length ? (
          clasesStruct.map((option, index) => renderSections(option, index))
        ) : (
          <MenuItem
            key={"menu-empty"}
            style={{ width: "100%" }}
            onClick={() => setAnchorEl(null)}
          >
            {"Sin datos para mostrar"}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default Selector;
